import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

import { useAuth } from "../context/AuthContext";

import TopNav from "../components/navigation/TopNav";
import StandardScreen, {
  ScreenContent,
} from "../components/layout/StandardScreen";
import Stack from "../components/layout/Stack";
import Card from "../components/ui/Card";
import FormField from "../components/forms/FormField";
import {
  TableShell,
  Table,
  Th,
  Td,
  NumberTd,
} from "../components/data/Table";

const narrowShellStyle = {
  width: "100%",
  maxWidth: "1100px",
  margin: "0 auto",
};

const errorTextStyle = {
  color: "var(--color-danger)",
};

const disabledInputStyle = {
  width: "100%",
  minHeight: "40px",
  padding: "0 12px",
  border: "1px solid var(--color-border, #d0d7de)",
  borderRadius: "8px",
  backgroundColor: "var(--color-surface-muted, #f6f8fa)",
  color: "var(--color-text, inherit)",
  fontSize: "var(--font-size-md)",
  opacity: 1,
};

const clickableRowButtonStyle = {
  appearance: "none",
  border: 0,
  background: "transparent",
  padding: 0,
  margin: 0,
  color: "var(--color-text, inherit)",
  cursor: "pointer",
  font: "inherit",
  textAlign: "left",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  width: "100%",
};

const selectedRowButtonStyle = {
  ...clickableRowButtonStyle,
  fontWeight: "var(--font-weight-semibold)",
};

const triangleStyle = {
  flex: "0 0 auto",
  fontSize: "0.9em",
  lineHeight: 1,
};

const memberButtonTextStyle = {
  display: "block",
  minWidth: 0,
  maxWidth: "30vw",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const billButtonTextStyle = {
  display: "block",
  minWidth: 0,
  maxWidth: "25vw",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const memberCellInnerStyle = {
  display: "block",
  width: "100%",
  maxWidth: "25vw",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const ExcursionTable = () => {
  const { t } = useTranslation();
  const { user, loadingUser } = useAuth();

  const memberDetailsRef = useRef(null);
  const billDetailsRef = useRef(null);

  const [excursions, setExcursions] = useState([]);
  const [selectedExcursion, setSelectedExcursion] = useState("");

  const [membersSummary, setMembersSummary] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [selectedMemberId, setSelectedMemberId] = useState("");
  const [selectedBillId, setSelectedBillId] = useState("");

  const [bills, setBills] = useState([]);

  const [loadingExcursions, setLoadingExcursions] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [loadingBills, setLoadingBills] = useState(false);
  const [excursionsError, setExcursionsError] = useState("");
  const [reportError, setReportError] = useState("");
  const [billsError, setBillsError] = useState("");

  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchExcursions = async () => {
      setLoadingExcursions(true);
      setExcursionsError("");

      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/excursions/i-can-see`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const data = Array.isArray(res.data) ? res.data : [];
        setExcursions(data);

        if (user?.excursion?.excursion_id) {
          const activeId = String(user.excursion.excursion_id);
          const existsInList = data.some(
            (exc) => String(exc.excursion_id) === activeId
          );

          if (existsInList) {
            setSelectedExcursion(activeId);
          }
        }
      } catch (err) {
        console.error("Failed to fetch excursions", err);
        setExcursionsError(t("full_details.errors.load_excursions"));
      } finally {
        setLoadingExcursions(false);
      }
    };

    if (token && user) {
      fetchExcursions();
    }
  }, [token, user, t]);

  useEffect(() => {
    if (!selectedExcursion || !token) {
      setMembersSummary([]);
      setGrandTotal(0);
      setReportError("");
      setSelectedMemberId("");
      setSelectedBillId("");
      return;
    }

    const fetchReport = async () => {
      setLoadingReport(true);
      setReportError("");

      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/bills/users-netted/${selectedExcursion}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        setMembersSummary(res.data?.members_summary || []);
        setGrandTotal(res.data?.grand_total || 0);
      } catch (err) {
        console.error("Failed to fetch report", err);
        setMembersSummary([]);
        setGrandTotal(0);
        setReportError(t("full_details.errors.load_report"));
      } finally {
        setLoadingReport(false);
      }
    };

    fetchReport();
  }, [selectedExcursion, token, t]);

  useEffect(() => {
    if (!selectedExcursion || !user || !token) {
      setBills([]);
      setBillsError("");
      setSelectedBillId("");
      return;
    }

    const fetchBills = async () => {
      setLoadingBills(true);
      setBillsError("");

      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/bills/all-bills-of-excursion/${selectedExcursion}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        setBills(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Failed to fetch bills", err);
        setBills([]);
        setBillsError(t("full_details.errors.load_bills"));
      } finally {
        setLoadingBills(false);
      }
    };

    fetchBills();
  }, [selectedExcursion, user, token, t]);

  const rows = useMemo(() => {
    return membersSummary.map((member, index) => {
      const details = member.details || [];

      const totalPaid = details.reduce(
        (sum, detail) => sum + Number(detail.amount_paid || 0),
        0
      );

      const totalReceived = details.reduce(
        (sum, detail) => sum + Number(detail.amount_benefited || 0),
        0
      );

      return {
        ...member,
        _rowId: String(member.user_id ?? member.member_id ?? index),
        total_paid: totalPaid,
        total_received: totalReceived,
        total_netted: Number(member.total_netted || 0),
        bill_count: member.bill_count ?? details.length ?? 0,
        details,
      };
    });
  }, [membersSummary]);

  const billRows = useMemo(() => {
    return bills.map((bill, index) => {
      const payerMap = {};
      const beneficiaryMap = {};

      (bill.payers || []).forEach((payer) => {
        const key = payer.user_id;
        payerMap[key] = {
          user_id: payer.user_id,
          member_name: `${payer.name || ""} ${payer.surname || ""}`.trim(),
          amount_paid: Number(payer.share || 0),
          received_monetary_share: 0,
        };
      });

      (bill.beneficiaries || []).forEach((beneficiary) => {
        const key = beneficiary.user_id;
        const received = Number(beneficiary.monetary_share || 0);

        if (payerMap[key]) {
          payerMap[key].received_monetary_share = received;
        } else {
          beneficiaryMap[key] = {
            user_id: beneficiary.user_id,
            member_name: `${beneficiary.name || ""} ${
              beneficiary.surname || ""
            }`.trim(),
            amount_paid: 0,
            received_monetary_share: received,
          };
        }
      });

      const details = [
        ...Object.values(payerMap),
        ...Object.values(beneficiaryMap),
      ];

      return {
        _rowId: String(bill.bill_id ?? bill.id ?? index),
        bill_id: bill.bill_id,
        definition: bill.definition ?? "—",
        amount: Number(bill.amount || 0),
        bill_date: bill.bill_date ?? "—",
        details,
      };
    });
  }, [bills]);

  useEffect(() => {
    if (!selectedMemberId) {
      setSelectedBillId("");
      return;
    }

    const stillExists = rows.some(
      (member) => String(member._rowId) === String(selectedMemberId)
    );

    if (!stillExists) {
      setSelectedMemberId("");
      setSelectedBillId("");
    }
  }, [rows, selectedMemberId]);

  useEffect(() => {
    if (!selectedBillId) return;

    const stillExists = billRows.some(
      (row) => String(row._rowId) === String(selectedBillId)
    );

    if (!stillExists) {
      setSelectedBillId("");
    }
  }, [billRows, selectedBillId]);

  const selectedMemberData = rows.find(
    (member) => String(member._rowId) === String(selectedMemberId)
  );

  const selectedBillData = billRows.find(
    (row) => String(row._rowId) === String(selectedBillId)
  );

  const handleMemberClick = (memberId) => {
    const willOpen = String(selectedMemberId) !== String(memberId);

    setSelectedBillId("");
    setSelectedMemberId(willOpen ? String(memberId) : "");

    if (willOpen) {
      window.setTimeout(() => {
        memberDetailsRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "start",
        });
      }, 0);
    }
  };

  const handleBillClick = (billId) => {
    const willOpen = String(selectedBillId) !== String(billId);

    setSelectedBillId(willOpen ? String(billId) : "");

    if (willOpen) {
      window.setTimeout(() => {
        billDetailsRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "start",
        });
      }, 0);
    }
  };

  if (loadingUser) {
    return (
      <StandardScreen
        topNav={<TopNav title={t("full_details.title")} mode="main" />}
        isLoading
      />
    );
  }

  if (!user) {
    return (
      <StandardScreen
        topNav={<TopNav title={t("full_details.title")} mode="main" />}
        isError
        errorState={
          <div style={errorTextStyle}>
            {t("full_details.errors.no_session")}
          </div>
        }
      />
    );
  }

  const allowedStatuses = ["approved", "pending", "suspended"];
  const isEligible =
    user.role === "admin" ||
    allowedStatuses.includes(String(user.subscription_status || ""));

  if (!isEligible) {
    return (
      <StandardScreen
        topNav={<TopNav title={t("full_details.title")} mode="main" />}
      >
        <ScreenContent>
          <Card variant="accent">
            <strong>{t("full_details.errors.no_access")}</strong>
          </Card>
        </ScreenContent>
      </StandardScreen>
    );
  }

  const selectedExcursionData = excursions.find(
    (exc) => String(exc.excursion_id) === String(selectedExcursion)
  );

  const selectedExcursionLabel = selectedExcursionData
    ? `${selectedExcursionData.short_name} (${selectedExcursionData.start_date} → ${selectedExcursionData.end_date})`
    : loadingExcursions
    ? t("full_details.loading_excursions")
    : t("full_details.no_excursion");

  return (
    <StandardScreen
      topNav={<TopNav title={t("full_details.title")} mode="main" />}
    >
      <ScreenContent>
        <div style={narrowShellStyle}>
          <Stack gap="5">
            <Card title={t("full_details.all_members_card.excursion")}>
              <FormField>
                <input
                  type="text"
                  value={selectedExcursionLabel}
                  disabled
                  readOnly
                  style={disabledInputStyle}
                />
              </FormField>

              {excursionsError ? (
                <div style={errorTextStyle}>{excursionsError}</div>
              ) : null}
            </Card>

            {selectedExcursion && (loadingReport || loadingBills) ? (
              <Card title={t("full_details.all_members_card.title")}>
                <div>{t("full_details.loading_report")}</div>
              </Card>
            ) : null}

            {reportError ? (
              <Card title={t("full_details.all_members_card.title")}>
                <div style={errorTextStyle}>{reportError}</div>
              </Card>
            ) : null}

            {billsError ? (
              <Card title={t("full_details.all_members_card.title")}>
                <div style={errorTextStyle}>{billsError}</div>
              </Card>
            ) : null}

            {selectedExcursion &&
            !loadingReport &&
            !reportError &&
            rows.length > 0 ? (
              <Card
                title={t("full_details.all_members_card.title")}
                subtitle={t("full_details.all_members_card.subtitle", {
                  total: Number(
                    (bills || []).reduce(
                      (sum, bill) => sum + Number(bill.amount || 0),
                      0
                    )
                  ).toFixed(2),
                })}
              >
                <TableShell maxHeight="40vh">
                  <Table>
                    <thead>
                      <tr>
                        <Th columnIndex={0} sticky>
                          {t("full_details.all_members_table.member")}
                        </Th>

                        <Th align="right">
                          {t("full_details.all_members_table.paid")}
                        </Th>

                        <Th align="right">
                          {t("full_details.all_members_table.received")}
                        </Th>

                        <Th align="right">
                          {t("full_details.all_members_table.net")}
                        </Th>

                        <Th align="right">
                          {t("full_details.all_members_table.bills")}
                        </Th>
                      </tr>
                    </thead>

                    <tbody>
                      {rows.map((member) => {
                        const isSelected =
                          String(member._rowId) === String(selectedMemberId);

                        return (
                          <tr key={member._rowId}>
                            <Td columnIndex={0} sticky>
                              <button
                                type="button"
                                onClick={() => handleMemberClick(member._rowId)}
                                style={
                                  isSelected
                                    ? selectedRowButtonStyle
                                    : clickableRowButtonStyle
                                }
                                title={member.member_name ?? "—"}
                              >
                                <span style={triangleStyle}>▶</span>
                                <span style={memberButtonTextStyle}>
                                  {member.member_name ?? "—"}
                                </span>
                              </button>
                            </Td>

                            <NumberTd>
                              {Number(member.total_paid || 0).toFixed(2)}
                            </NumberTd>

                            <NumberTd>
                              {Number(member.total_received || 0).toFixed(2)}
                            </NumberTd>

                            <NumberTd>
                              {Number(member.total_netted || 0).toFixed(2)}
                            </NumberTd>

                            <NumberTd>{member.bill_count ?? 0}</NumberTd>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </TableShell>
              </Card>
            ) : null}

            {selectedExcursion &&
            !loadingReport &&
            !reportError &&
            rows.length === 0 ? (
              <Card title={t("full_details.all_members_card.title")}>
                <div>{t("full_details.messages.no_data")}</div>
              </Card>
            ) : null}

            {selectedMemberData ? (
              <div ref={memberDetailsRef}>
                <Card
                  title={
                    selectedMemberData.member_name ??
                    t("full_details.all_members_table.member")
                  }
                  subtitle={`${t("full_details.subtitles.total_paid")}: ${Number(
                    selectedMemberData.total_paid || 0
                  ).toFixed(2)}, ${t(
                    "full_details.subtitles.total_received"
                  )}: ${Number(selectedMemberData.total_received || 0).toFixed(
                    2
                  )}, ${t("full_details.subtitles.net")}: ${Number(
                    selectedMemberData.total_netted || 0
                  ).toFixed(2)}`}
                >
                  {selectedMemberData.details?.length > 0 ? (
                    <TableShell maxHeight="30vh">
                      <Table>
                        <thead>
                          <tr>
                            <Th columnIndex={0} sticky>
                              {t("full_details.member_table.bill_id")}
                            </Th>

                            <Th>
                              {t("full_details.member_table.definition")}
                            </Th>

                            <Th align="right">
                              {t("full_details.member_table.paid")}
                            </Th>

                            <Th align="right">
                              {t("full_details.member_table.received")}
                            </Th>

                            <Th align="right">
                              {t("full_details.member_table.net")}
                            </Th>
                          </tr>
                        </thead>

                        <tbody>
                          {selectedMemberData.details.map((detail, index) => {
                            const billRowId = String(
                              detail.bill_id ?? detail.id ?? `${index}`
                            );
                            const isSelected =
                              billRowId === String(selectedBillId);

                            return (
                              <tr
                                key={
                                  detail.bill_id ??
                                  detail.id ??
                                  `${selectedMemberData._rowId}-${index}`
                                }
                              >
                                <Td columnIndex={0} sticky>
                                  <button
                                    type="button"
                                    onClick={() => handleBillClick(billRowId)}
                                    style={
                                      isSelected
                                        ? selectedRowButtonStyle
                                        : clickableRowButtonStyle
                                    }
                                    title={detail.bill_id ?? "—"}
                                  >
                                    <span style={triangleStyle}>▶</span>
                                    <span style={billButtonTextStyle}>
                                      {detail.bill_id ?? "—"}
                                    </span>
                                  </button>
                                </Td>

                                <Td title={detail.definition ?? "—"}>
                                  {detail.definition ?? "—"}
                                </Td>

                                <NumberTd>
                                  {Number(detail.amount_paid || 0).toFixed(2)}
                                </NumberTd>

                                <NumberTd>
                                  {Number(detail.amount_benefited || 0).toFixed(2)}
                                </NumberTd>

                                <NumberTd>
                                  {Number(detail.amount_netted || 0).toFixed(2)}
                                </NumberTd>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </TableShell>
                  ) : (
                    <div>{t("full_details.messages.no_data")}</div>
                  )}
                </Card>
              </div>
            ) : null}

            {selectedBillData ? (
              <div ref={billDetailsRef}>
                <Card
                  title={
                    selectedBillData.definition ??
                    t("full_details.bill_card.title")
                  }
                subtitle={`${selectedBillData.bill_date ?? "—"} • ${t("full_details.subtitles.total_paid")}: ${Number(
                  selectedBillData.details?.reduce(
                    (sum, d) => sum + Number(d.amount_paid || 0),
                    0
                  ) || 0
                ).toFixed(2)} ${t("full_details.subtitles.by")} ${
                  (selectedBillData.details || [])
                    .filter((d) => Number(d.amount_paid || 0) > 0)
                    .map((d) => d.member_name)
                    .join(", ") || "—"
                }`}
                >
                  {selectedBillData.details?.length > 0 ? (
                    <TableShell maxHeight="30vh">
                      <Table>
                        <thead>
                          <tr>
                            <Th columnIndex={0} sticky>
                              {t("full_details.bill_table.member")}
                            </Th>

                            <Th align="right">
                              {t("full_details.bill_table.paid")}
                            </Th>

                            <Th align="right">
                              {t("full_details.bill_table.received")}
                            </Th>

                          </tr>
                        </thead>

                        <tbody>
                          {selectedBillData.details.map((detail, index) => (
                            <tr
                              key={
                                detail.user_id ??
                                detail.id ??
                                `${selectedBillData._rowId}-${index}`
                              }
                            >
                              <Td
                                columnIndex={0}
                                sticky
                                title={detail.member_name ?? "—"}
                              >
                                <span style={memberCellInnerStyle}>
                                  {detail.member_name ?? "—"}
                                </span>
                              </Td>

                              <NumberTd>
                                {Number(detail.amount_paid || 0).toFixed(2)}
                              </NumberTd>

                              <NumberTd>
                                {Number(
                                  detail.received_monetary_share || 0
                                ).toFixed(2)}
                              </NumberTd>

                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </TableShell>
                  ) : (
                    <div>{t("full_details.messages.no_data")}</div>
                  )}
                </Card>
              </div>
            ) : null}
          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
};

export default ExcursionTable;